import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TrendingUp, TrendingDown, Minus, RefreshCw, ShoppingCart, Star } from "lucide-react";
import { cn } from "@/lib/cn";
import { uahFull, dmy } from "@/lib/client360Labels";
import {
  fetchClient360Purchases,
  requestPurchasesRefresh,
  type Client360PurchasesResponse,
  type PurchasesBrand,
} from "@/api/purchases";

/**
 * PB-2 «Купує у нас» (TZ_PB2_CustomerPurchases_v1_0_LOCK §C) — ONE reusable
 * component (MP4): compact в Overview, full у Profile. Read-only auto-BI
 * (D-049), дані з CRM-кешу (НЕ live-BI per view).
 *
 * Правила рендеру:
 *  • F1 tolerant: бренд = brand_name з purchases-рядка (позакаталожні хвости
 *    рендеряться нормально, 0 битих);
 *  • null → «—», НІКОЛИ 0/NaN (cycle_days/trend/aov/last_purchase);
 *  • scope-лейбл з memberCount (НЕ зі scope-поля — BI віддає різний scope для
 *    head- vs member-кода одного analytical client, F2): >1 → «Холдинг · N»;
 *  • «Інше / без бренду» = 100−Σshare, ЛИШЕ якщо ≥1% (AC-PB2-015);
 *  • no_data → живий стан (не мертвий блок); pending → «Дані завантажуються»;
 *  • refresh — backend-owned allowedActions (ACL==читання + rate-limit).
 */

const OTHER_THRESHOLD_PCT = 1;

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function dash<T>(v: T | null | undefined, fmt: (x: T) => string): string {
  return v === null || v === undefined || v === "" ? "—" : fmt(v);
}

function TrendIcon({ trend }: { trend: string | null | undefined }) {
  if (trend === "up") return <TrendingUp className="h-3.5 w-3.5 text-success-foreground" />;
  if (trend === "down") return <TrendingDown className="h-3.5 w-3.5 text-destructive" />;
  if (trend === "flat") return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
  return <span className="text-muted-foreground">—</span>;
}

function scopeLabel(p: Client360PurchasesResponse): string {
  const n = p.memberCount ?? 1;
  return n > 1 ? `Холдинг · ${n} учасників` : "Окремий клієнт";
}

function staleBadge(mart: string | null | undefined): boolean {
  if (!mart) return false;
  const ageMs = Date.now() - new Date(mart).getTime();
  return ageMs > 1000 * 60 * 60 * 48; // >48h = stale
}

function BrandRow({ b, compact }: { b: PurchasesBrand; compact?: boolean }) {
  const share = num(b.share_pct);
  return (
    <div className="flex items-center gap-2 border-t py-1.5 first:border-t-0">
      <div className="min-w-0 flex-1 truncate text-[12.5px] font-medium">
        {b.brand_name}
        {b.is_strategic && <Star className="ml-1 inline h-3 w-3 fill-warning-foreground text-warning-foreground" aria-label="стратегічний" />}
      </div>
      <div className="shrink-0 text-[12px] tabular-nums text-muted-foreground">
        {dash(share, (s) => `${s.toFixed(s >= 10 ? 0 : 1)}%`)}
      </div>
      {!compact && (
        <>
          <div className="w-24 shrink-0 text-right text-[12px] tabular-nums">{dash(num(b.turnover_uah), uahFull)}</div>
          <div className="w-20 shrink-0 text-right text-[11.5px] text-muted-foreground">{dash(b.last_purchase_date, dmy)}</div>
        </>
      )}
    </div>
  );
}

export function CustomerPurchasesSummary({ holdingId, variant }: {
  holdingId: string;
  variant: "compact" | "full";
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["client360Purchases", holdingId],
    queryFn: ({ signal }) => fetchClient360Purchases(holdingId, signal),
    staleTime: 60_000,
  });

  const refreshM = useMutation({
    mutationFn: () => requestPurchasesRefresh(holdingId),
    onSettled: () => qc.invalidateQueries({ queryKey: ["client360Purchases", holdingId] }),
  });

  const p = q.data;
  const compact = variant === "compact";

  return (
    <div className="rounded-xl border bg-card p-4" data-testid="purchases-summary">
      <div className="mb-1 flex items-center gap-2">
        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-[13px] font-semibold">Купує у нас</h3>
        {p && p.status !== "pending" && (
          <span className="rounded-md border px-1.5 py-0.5 text-[11px] text-muted-foreground">{scopeLabel(p)}</span>
        )}
        <span className="ml-auto" />
        {p?.allowedActions.canRefresh && (
          <button
            onClick={() => refreshM.mutate()}
            disabled={refreshM.isPending}
            title="Оновити з BI (через sync-чергу)"
            className="rounded-md border px-1.5 py-1 text-muted-foreground hover:bg-muted/40 disabled:opacity-50"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", refreshM.isPending && "animate-spin")} />
          </button>
        )}
      </div>

      {q.isLoading && <div className="py-4 text-center text-[12.5px] text-muted-foreground">Завантаження…</div>}
      {q.isError && <div className="py-4 text-center text-[12.5px] text-muted-foreground">Дані тимчасово недоступні</div>}

      {p && p.status === "pending" && (
        <div className="rounded-lg border border-dashed px-4 py-5 text-center text-[12.5px] text-muted-foreground">
          Дані завантажуються — зʼявляться після нічної синхронізації BI.
        </div>
      )}

      {p && p.status === "no_data" && (
        <div className="rounded-lg border border-dashed px-4 py-5 text-center text-[12.5px] text-muted-foreground">
          Немає B2B-покупок за 12 міс
          <div className="mt-1 text-[11px]">(read-only з BI — ручний ввід не передбачений)</div>
        </div>
      )}

      {p && p.status === "ok" && (() => {
        const brands = (p.brands ?? []).slice().sort((a, b) => (num(b.share_pct) ?? 0) - (num(a.share_pct) ?? 0));
        const shown = compact ? brands.slice(0, 3) : brands;
        const sumShare = brands.reduce((s, b) => s + (num(b.share_pct) ?? 0), 0);
        const other = Math.max(0, 100 - sumShare);
        return (
          <>
            <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
              <span className="font-semibold tabular-nums">{dash(num(p.totalTurnoverUah), uahFull)}</span>
              <span className="flex items-center gap-1 text-muted-foreground">тренд <TrendIcon trend={p.trend} /></span>
              <span className="text-muted-foreground">остання: {dash(p.lastPurchaseDate, dmy)}</span>
              {!compact && (
                <>
                  <span className="text-muted-foreground">цикл: {dash(p.cycleDays, (d) => `${d} дн`)}</span>
                  <span className="text-muted-foreground">покупок: {dash(p.frequency, String)}</span>
                  <span className="text-muted-foreground">сер. чек: {dash(num(p.aovUah), uahFull)}</span>
                  {p.period && <span className="text-muted-foreground">період: {p.period}</span>}
                </>
              )}
            </div>
            {shown.length === 0
              ? <div className="py-3 text-center text-[12.5px] text-muted-foreground">Брендових покупок немає</div>
              : shown.map((b, i) => <BrandRow key={`${b.brand_name}-${i}`} b={b} compact={compact} />)}
            {!compact && other >= OTHER_THRESHOLD_PCT && (
              <div className="flex items-center gap-2 border-t py-1.5 text-muted-foreground">
                <div className="min-w-0 flex-1 text-[12.5px]">Інше / без бренду</div>
                <div className="shrink-0 text-[12px] tabular-nums">{other.toFixed(1)}%</div>
                <div className="w-24 shrink-0" /><div className="w-20 shrink-0" />
              </div>
            )}
            {compact && brands.length > 3 && (
              <div className="pt-1 text-[11.5px] text-muted-foreground">+ ще {brands.length - 3} брендів — у Профілі</div>
            )}
          </>
        );
      })()}

      {p && (p.martSnapshotDate || p.refreshPending) && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
          {p.martSnapshotDate && <span>Дані станом на {dmy(p.martSnapshotDate)}</span>}
          {staleBadge(p.martSnapshotDate) && (
            <span className="rounded-md border border-warning-foreground/40 bg-warning px-1.5 py-0.5 text-warning-foreground">застарілі</span>
          )}
          {p.refreshPending && <span className="rounded-md border px-1.5 py-0.5">оновлення заплановано</span>}
          <span className="ml-auto">⟳ з BI · read-only</span>
        </div>
      )}
    </div>
  );
}
